
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, History, UserRound, RefreshCcw, MessageSquarePlus, Lock, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/components/AuthProvider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface MenuDropdownProps {
  onNewSession: () => void;
  onHistoryOpen: () => void;
  onProfileOpen: () => void;
  onFeedbackOpen: () => void;
  onPricingClick?: () => void;
}

export const MenuDropdown = ({
  onNewSession,
  onHistoryOpen,
  onProfileOpen,
  onFeedbackOpen,
  onPricingClick,
}: MenuDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const isAuthenticated = !!user;
  
  const handleMenuItemClick = (handler: () => void, requiresAuth = false) => {
    if (requiresAuth && !isAuthenticated) {
      // Don't execute the handler if authentication is required but user is not authenticated
      return;
    }
    handler();
    setIsOpen(false);
  };

  const handlePricingClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onPricingClick) {
      onPricingClick();
      setIsOpen(false);
    }
  };
  
  return (
    <TooltipProvider>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-8 w-8 rounded-lg hover:bg-gray-50/80 p-0 flex items-center gap-1 transition-all duration-300",
              isOpen && "bg-gray-50/80"
            )}
          >
            <div className="w-14 h-11 -ml-3 group transition-all duration-300 hover:scale-105 relative">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 opacity-0 group-hover:opacity-100 -z-10 blur-md transition-opacity"></div>
              <img 
                src="/lovable-uploads/8a7563bc-58a9-4c76-bab1-8a146b858908.png"
                alt="Medical Professional Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <ChevronDown className={cn(
              "h-3.5 w-3.5 text-indigo-400 transition-transform duration-300",
              isOpen ? "rotate-180" : "rotate-0"
            )} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={6}
          className="w-64 bg-white/95 backdrop-blur-xl border border-indigo-100/70 shadow-lg rounded-lg py-1.5 animate-fade-in"
        >
          <DropdownMenuItem 
            onClick={() => handleMenuItemClick(onNewSession)}
            className="flex items-center h-9 px-3 text-gray-700 hover:bg-indigo-50/80 hover:text-indigo-600 rounded-md mx-1 transition-colors"
          >
            <RefreshCcw className="h-4 w-4 mr-2.5 text-indigo-400" />
            <span className="text-sm">New Session</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator className="my-1.5 mx-1 bg-indigo-50" />
          
          {isAuthenticated ? (
            <DropdownMenuItem 
              onClick={() => handleMenuItemClick(onHistoryOpen)}
              className="flex items-center h-9 px-3 text-gray-700 hover:bg-indigo-50/80 hover:text-indigo-600 rounded-md mx-1 transition-colors"
            >
              <History className="h-4 w-4 mr-2.5 text-indigo-400" />
              <span className="text-sm">Drawing History</span>
            </DropdownMenuItem>
          ) : (
            <div className="flex items-center justify-between h-9 px-3 text-gray-400 rounded-md mx-1 cursor-not-allowed">
              <div className="flex items-center">
                <Lock className="h-4 w-4 mr-2.5 text-gray-400" />
                <span className="text-sm">Drawing History</span>
              </div>
              <button 
                onClick={handlePricingClick}
                className="min-w-[60px] h-[22px] bg-indigo-600 text-white text-[10px] font-medium rounded-full px-2 flex items-center justify-center"
              >
                <Sparkles className="h-2 w-2 mr-1" />
                Go Pro
              </button>
            </div>
          )}
          
          {isAuthenticated ? (
            <DropdownMenuItem 
              onClick={() => handleMenuItemClick(onProfileOpen)}
              className="flex items-center h-9 px-3 text-gray-700 hover:bg-indigo-50/80 hover:text-indigo-600 rounded-md mx-1 transition-colors"
            >
              <UserRound className="h-4 w-4 mr-2.5 text-indigo-400" />
              <span className="text-sm">Profile & Settings</span>
            </DropdownMenuItem>
          ) : (
            <div className="flex items-center justify-between h-9 px-3 text-gray-400 rounded-md mx-1 cursor-not-allowed">
              <div className="flex items-center">
                <Lock className="h-4 w-4 mr-2.5 text-gray-400" />
                <span className="text-sm">Profile & Settings</span>
              </div>
              <button 
                onClick={handlePricingClick}
                className="min-w-[60px] h-[22px] bg-indigo-600 text-white text-[10px] font-medium rounded-full px-2 flex items-center justify-center"
              >
                <Sparkles className="h-2 w-2 mr-1" />
                Go Pro
              </button>
            </div>
          )}
          
          <DropdownMenuSeparator className="my-1.5 mx-1 bg-indigo-50" />
          <DropdownMenuItem 
            onClick={() => handleMenuItemClick(onFeedbackOpen)}
            className="flex items-center h-9 px-3 text-gray-700 hover:bg-indigo-50/80 hover:text-indigo-600 rounded-md mx-1 transition-colors group"
          >
            <MessageSquarePlus className="h-4 w-4 mr-2.5 text-indigo-400 transition-transform duration-200 group-hover:scale-110" />
            <span className="text-sm">Share Feedback</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
};
